import marketingClient from '@/js/common/Domain/Service/marketingClient';
import type { IBackboneHotelOffer } from '@ve/types/backboneModels/Domain/ObjectValue';
import { OperatorId, isTravelataOperatorId, isAffiliateOperatorId } from '@entities/operator';
import { SupplierType, type ProviderOfferData, type OffersFunnel } from '../../model/types';

export interface SearchIds {
  hsid: string | null;
  sid: string | null;
}

export interface RoomProvidersAnalyticsContext {
  country_id: number | null;
  resort_id: number | null;
  hotel_id: number | null;
  room_category_id: number | null;
  hsid: string | null;
  sid: string | null;
  nights: number | null;
  adults: number | null;
  kids: number | null;
  guests: number | null;
  meal_id: number | null;
  client_uuid: string | null;
}

interface HotelPageAppConfig {
  hotelInfo?: {
    id?: number;
    country?: number;
    resort?: number;
  };
}

const SUPPLIER_NAME_BY_ID: Record<number, string> = {
  [OperatorId.Yandex]: 'yandex',
  [OperatorId.Travelata[0]]: 'travelata',
  [OperatorId.Travelata[1]]: 'travelata',
  [OperatorId.FunSun]: 'fun_sun',
  [OperatorId.TezTour]: 'tez_tour',
  [OperatorId.AnexTour]: 'anex_tour',
  [OperatorId.AleanTour]: 'alean_tour',
  [OperatorId.BiblioGlobus]: 'biblio_globus',
  [OperatorId.RussianExpress]: 'russian_express',
  [OperatorId.SunMar]: 'sun_mar',
  [OperatorId.SpaceTravel]: 'space_travel',
  [OperatorId.ChinaTravel]: 'china_travel',
  [OperatorId.OneTouchTravel]: 'one_touch_travel',
  [OperatorId.LetsFly]: 'lets_fly',
  [OperatorId.NTK]: 'ntk',
  [OperatorId.ICS]: 'ics',
  [OperatorId.Paks]: 'paks',
  [OperatorId.Loti]: 'loti',
};

export const OFFER_ACTION = {
  ROOM_BLOCK_OPEN: 'room_block_open',
  ROOM_BLOCK_CLOSE: 'room_block_close',
  BLOCK_IMPRESSION: 'block_impression',
  OFFER_CLICK: 'offer_click',
  MODAL_CONFIRM_CLICK: 'modal_confirm_click',
  MODAL_STAY_CLICK: 'modal_stay_click',
  TARIFFS_OPEN: 'tariffs_open',
  TARIFFS_CLICK: 'tariffs_click',
  TARIFFS_CLOSE: 'tariffs_close',
  TRUST_TOOLTIP_OPEN: 'trust_tooltip_open',
  TRUST_TOOLTIP_CLOSE: 'trust_tooltip_close',
};

export const getOfferGoal = (funnel: OffersFunnel, action: string): string => `hotelpage_${funnel}_offers_${action}`;

export const getSupplierType = (operatorId: number): SupplierType => {
  if (isTravelataOperatorId(operatorId)) return SupplierType.Own;
  if (isAffiliateOperatorId(operatorId)) return SupplierType.Affiliate;
  return SupplierType.Operator;
};

export const getSupplierName = (provider: ProviderOfferData): string =>
  SUPPLIER_NAME_BY_ID[provider.operatorId] ?? `operator_${provider.operatorId}`;

export const getMinimalPrice = (providers: ProviderOfferData[]): number | null =>
  providers.length > 0 ? Math.min(...providers.map((provider) => provider.price)) : null;

export const getTravelataPrice = (providers: ProviderOfferData[]): number | null =>
  providers.find((provider) => isTravelataOperatorId(provider.operatorId))?.price ?? null;

export const buildSuppliersMap = (providers: ProviderOfferData[]): Record<string, string> =>
  providers
    .filter((provider) => provider && !isTravelataOperatorId(provider.operatorId))
    .reduce<Record<string, string>>((acc, provider, index) => {
      acc[index + 1] = `${getSupplierName(provider)}:${provider.price}`;
      return acc;
    }, {});

export const getSupplierPosition = (providers: ProviderOfferData[], provider: ProviderOfferData): number =>
  providers.findIndex((item) => item.operatorId === provider.operatorId) + 1;

const getNightsCount = (product: IBackboneHotelOffer | null | undefined): number | null => {
  const nights = product?.get('nights') as number | { tour?: number };
  return typeof nights === 'number' ? nights : nights?.tour ?? null;
};

export const buildRoomProvidersAnalyticsContext = (
  product: IBackboneHotelOffer | null | undefined,
  searchIds: SearchIds,
): RoomProvidersAnalyticsContext => {
  const hotelInfo = (window as unknown as { appConfig?: HotelPageAppConfig }).appConfig?.hotelInfo;
  const touristGroup = product?.get('touristGroup') ?? null;
  const room = product?.get('room') ?? null;
  const meal = product?.get('meal') ?? null;
  const adults = touristGroup?.adults ?? null;
  const kids = touristGroup?.kids ?? null;
  const guests = touristGroup ? (adults ?? 0) + (kids ?? 0) : null;

  return {
    country_id: hotelInfo?.country ?? null,
    resort_id: hotelInfo?.resort ?? null,
    hotel_id: product?.get('hotel') ?? hotelInfo?.id ?? null,
    room_category_id: room?.category ?? null,
    hsid: searchIds.hsid,
    sid: searchIds.sid,
    nights: getNightsCount(product),
    adults,
    kids,
    guests,
    meal_id: meal?.get('id') ?? null,
    client_uuid: marketingClient?.getClientUuid() ?? null,
  };
};
